/**
 * F41 (rev-12 follow-up) — admin bracket-management dialogs for one division.
 *
 *   - <SeedDivisionDialog>     POST /admin/tournaments/:id/divisions/:divId/seed
 *     (method: random | skill — manual seeding needs an ordered list UI and
 *     stays organizer-side at MVP)
 *   - <GenerateBracketDialog>  POST /admin/tournaments/:id/divisions/:divId/bracket
 *
 * Both gated on `tournaments:manage_bracket` by the detail page (the server
 * re-enforces). Composes F02 primitives only; mirrors the reason-dialog shape
 * of `TournamentReasonDialogs.tsx`.
 */
import { useEffect, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, Select } from '@bravemove/ui-web';
import { adminTournamentsApi } from '../../api/tournaments';
import { translateApiError } from '../../auth/errors';

// ─── Seed ────────────────────────────────────────────────────────────────────

export interface SeedDivisionDialogProps {
  readonly open: boolean;
  readonly tournamentId: string;
  readonly divisionId: string;
  readonly divisionName: string;
  readonly onClose: () => void;
  readonly onDone: () => void | Promise<void>;
}

export function SeedDivisionDialog({
  open,
  tournamentId,
  divisionId,
  divisionName,
  onClose,
  onDone,
}: SeedDivisionDialogProps) {
  const { t } = useI18n();
  const [method, setMethod] = useState<'random' | 'skill'>('random');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setMethod('random');
    setError(null);
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    setError(null);
    try {
      await adminTournamentsApi.seedDivision(tournamentId, divisionId, { method });
      await onDone();
      onClose();
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:tournament.seedDialog.title', { defaultValue: 'Seed division' })}
      description={divisionName}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting} data-testid="seed-cancel">
            {t('admin:tournament.seedDialog.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={submitting}
            data-testid="seed-submit"
          >
            {t('admin:tournament.seedDialog.confirm', { defaultValue: 'Seed' })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="seed-division-dialog">
        {error ? <Banner tone="danger">{error}</Banner> : null}
        <FormField
          label={t('admin:tournament.seedDialog.methodLabel', { defaultValue: 'Seeding method' })}
        >
          <Select
            value={method}
            onChange={(e) => setMethod(e.target.value as 'random' | 'skill')}
            options={[
              {
                value: 'random',
                label: t('tournament:seeding_method.random', { defaultValue: 'Random' }),
              },
              {
                value: 'skill',
                label: t('tournament:seeding_method.skill', { defaultValue: 'By skill' }),
              },
            ]}
            data-testid="seed-method-select"
          />
        </FormField>
      </div>
    </Modal>
  );
}

// ─── Generate ────────────────────────────────────────────────────────────────

export interface GenerateBracketDialogProps {
  readonly open: boolean;
  readonly tournamentId: string;
  readonly divisionId: string;
  readonly divisionName: string;
  readonly onClose: () => void;
  readonly onDone: () => void | Promise<void>;
}

export function GenerateBracketDialog({
  open,
  tournamentId,
  divisionId,
  divisionName,
  onClose,
  onDone,
}: GenerateBracketDialogProps) {
  const { t } = useI18n();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setError(null);
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    setError(null);
    try {
      await adminTournamentsApi.generateBracket(tournamentId, divisionId);
      await onDone();
      onClose();
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:tournament.generateDialog.title', { defaultValue: 'Generate bracket' })}
      description={divisionName}
      size="sm"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="generate-cancel"
          >
            {t('admin:tournament.generateDialog.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={submitting}
            data-testid="generate-submit"
          >
            {t('admin:tournament.generateDialog.confirm', { defaultValue: 'Generate' })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="generate-bracket-dialog">
        {error ? <Banner tone="danger">{error}</Banner> : null}
        <p className="text-[13px] text-ink-2">
          {t('admin:tournament.generateDialog.body', {
            defaultValue:
              'Builds the bracket/schedule from the confirmed roster. Idempotent until a match is final (then the bracket is locked).',
          })}
        </p>
      </div>
    </Modal>
  );
}
